import { useState, useEffect } from 'react';
import { inviteCodes, users } from '../services/api';
import LayoutV2 from '../components/LayoutV2';

export default function InviteCodesV2() {
  const [allCodes, setAllCodes] = useState([]);
  const [availableCodes, setAvailableCodes] = useState([]);
  const [promoters, setPromoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPromoter, setSelectedPromoter] = useState('');
  const [selectedCode, setSelectedCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchedUser, setSearchedUser] = useState(null);
  const [searchedCodes, setSearchedCodes] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [codesRes, availableRes, usersRes] = await Promise.all([
        inviteCodes.list(),
        inviteCodes.getAvailable(),
        users.list()
      ]);
      setAllCodes(codesRes.data.inviteCodes || []);
      setAvailableCodes(availableRes.data.availableCodes || []);
      // Include Director (tier 1), Manager (tier 2), and Promoter (tier 3)
      setPromoters(
        (usersRes.data.users || [])
          .filter(u => u.tier >= 1 && u.tier <= 3)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      );
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignCode = async (e) => {
    e.preventDefault();
    if (!selectedPromoter || !selectedCode) {
      setMessage({ type: 'error', text: 'Please select both user and code' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      await inviteCodes.create({
        promoterId: selectedPromoter,
        inviteCode: selectedCode
      });
      setMessage({ type: 'success', text: 'Invite code assigned successfully!' });
      setShowModal(false);
      setSelectedPromoter('');
      setSelectedCode('');
      loadData();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to assign code' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (code) => {
    try {
      const nextIsActive = !code.is_active;
      setMessage({ type: '', text: '' });
      await inviteCodes.update(code.id, { isActive: nextIsActive });
      await loadData();
      setMessage({
        type: 'success',
        text: nextIsActive ? 'Invite code activated successfully.' : 'Invite code deactivated successfully.'
      });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update code' });
    }
  };

  const handleSearchByUser = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    
    const search = searchTerm.trim().toLowerCase();
    
    // First, search for matching invite codes
    const matchingCodes = allCodes.filter(code => 
      code.invite_code.toLowerCase().includes(search) ||
      (code.promoter_username && code.promoter_username.toLowerCase().includes(search)) ||
      (code.promoter_name && code.promoter_name.toLowerCase().includes(search))
    );
    
    if (matchingCodes.length > 0) {
      // Select primary result: exact invite code match first, then most recent
      let primaryCode = matchingCodes[0];
      
      // Prefer exact invite code match
      const exactCodeMatch = matchingCodes.find(code => 
        code.invite_code.toLowerCase() === search
      );
      if (exactCodeMatch) {
        primaryCode = exactCodeMatch;
      }
      
      // Find user for the primary code
      const user = promoters.find(p => p.id === primaryCode.promoter_id);
      setSearchedUser(user || null);
      setSearchedCodes([primaryCode]);
      setMessage({ type: '', text: '' });
    } else {
      // Fallback to original user search for backward compatibility
      const user = promoters.find(p => 
        p.username.toLowerCase().includes(search) || 
        (p.displayName && p.displayName.toLowerCase().includes(search))
      );
      
      if (user) {
        setSearchedUser(user);
        // Get all invite codes for this user
        const codes = allCodes.filter(c => c.promoter_id === user.id);
        setSearchedCodes(codes);
        setMessage({ type: '', text: '' });
      } else {
        setSearchedUser(null);
        setSearchedCodes([]);
        setMessage({ type: 'error', text: 'No matching referral code or user found' });
      }
    }
  };

  const getTierName = (tier) => {
    const names = { 1: 'Director', 2: 'Manager', 3: 'Promoter' };
    return names[tier] || 'Unknown';
  };

  // Use data directly from API response (promoter_username, promoter_name)
  const getPromoterName = (code) => {
    const username = code.promoter_username || 'Unknown';
    const displayName = code.promoter_name || username;
    return `${displayName} (@${username})`;
  };

  if (loading) {
    return (
      <LayoutV2>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-400">Loading...</div>
        </div>
      </LayoutV2>
    );
  }

  return (
    <LayoutV2>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Referral Code Management</h1>
            <p className="text-slate-400 mt-1">Assign referral codes to Directors, Managers, and Promoters</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            + Assign New Referral Code
          </button>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg ${message.type === 'error' ? 'bg-red-900/50 text-red-400' : 'bg-green-900/50 text-green-400'}`}>
            {message.text}
          </div>
        )}

        {/* Search Referral Codes */}
        <div className="rounded-xl border border-slate-700 bg-[#1e293b] p-4">
          <h2 className="font-semibold text-white mb-3">Search Referral Codes</h2>
          <form onSubmit={handleSearchByUser} className="flex gap-3">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
               placeholder="Enter referral code, username, or display name"
              className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
            >
              Search
            </button>
          </form>
          
          {searchedUser && (
            <div className="mt-4 p-3 bg-slate-800 rounded-lg">
              <p className="text-white">
                <span className="text-slate-400">User: </span>
                {searchedUser.displayName || searchedUser.username} (@{searchedUser.username})
                <span className="ml-2 px-2 py-0.5 bg-cyan-900 text-cyan-300 rounded text-xs">
                  {getTierName(searchedUser.tier)}
                </span>
              </p>
              {searchedCodes.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {searchedCodes.map(code => (
                    <span key={code.id} className="px-3 py-1 bg-slate-700 text-white rounded font-mono">
                      {code.invite_code}
                      {!code.is_active && <span className="ml-1 text-red-400">(Inactive)</span>}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-slate-400">No referral codes assigned to this user</p>
              )}
            </div>
          )}
        </div>

        {/* Assigned Codes */}
        <div className="rounded-xl border border-slate-700 bg-[#1e293b] overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700">
            <h2 className="font-semibold text-white">All Assigned Referral Codes</h2>
          </div>
          {allCodes.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              No referral codes assigned yet. Click "Assign New Referral Code" to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Assigned To</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {allCodes.map((code) => (
                    <tr key={code.id} className="hover:bg-slate-800/30">
                      <td className="px-4 py-3">
                        <span className="font-mono text-white font-medium">{code.invite_code}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {getPromoterName(code)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          code.is_active 
                            ? 'bg-green-900/50 text-green-400' 
                            : 'bg-red-900/50 text-red-400'
                        }`}>
                          {code.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-sm">
                        {new Date(code.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleToggleActive(code)}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            code.is_active
                              ? 'bg-red-900/50 hover:bg-red-900 text-red-400'
                              : 'bg-green-900/50 hover:bg-green-900 text-green-400'
                          }`}
                        >
                          {code.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Available Codes from External DB */}
        <div className="rounded-xl border border-slate-700 bg-[#1e293b] overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700">
            <h2 className="font-semibold text-white">Available Referral Codes (from External DB)</h2>
            <p className="text-xs text-slate-400 mt-1">{availableCodes.length} codes available to assign</p>
          </div>
          {availableCodes.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              No available codes in the external database.
            </div>
          ) : (
            <div className="p-4 max-h-64 overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {availableCodes.slice(0, 50).map((code) => (
                  <span
                    key={code.id}
                    className="px-2 py-1 bg-slate-700 text-slate-300 rounded text-sm font-mono"
                  >
                    {code.invite_code}
                  </span>
                ))}
                {availableCodes.length > 50 && (
                  <span className="px-2 py-1 text-slate-500 text-sm">
                    +{availableCodes.length - 50} more...
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#1e293b] rounded-xl border border-slate-700 w-full max-w-md p-6">
            <h3 className="text-xl font-bold text-white mb-4">Assign Referral Code</h3>
            <form onSubmit={handleAssignCode}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-400 mb-2">Select User</label>
                <select
                  value={selectedPromoter}
                  onChange={(e) => setSelectedPromoter(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
                  required
                >
                  <option value="">-- Select User --</option>
                  {promoters.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName} (@{p.username}) - {getTierName(p.tier)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-400 mb-2">Select Referral Code</label>
                <select
                  value={selectedCode}
                  onChange={(e) => setSelectedCode(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white font-mono"
                  required
                >
                  <option value="">-- Select Code --</option>
                  {availableCodes.map((c) => (
                    <option key={c.id} value={c.invite_code}>
                      {c.invite_code}
                    </option>
                  ))}
                </select>
              </div>
              {message.type === 'error' && (
                <p className="text-red-400 text-sm mb-4">{message.text}</p>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setMessage({ type: '', text: '' });
                  }}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </LayoutV2>
  );
}
